/**
 * 商品マスタ抽出APIクライアント
 * バックエンドの /{accountCode}/product-master/preview エンドポイントを呼び出す
 */

import { apiGet } from './client';
import type { ProductMasterPreview } from '@/lib/types/feature/product-master';
import type {
  ProductGroupSelection,
  ProductMasterColumnGroup,
} from '@/lib/types/feature/analysis-data-extraction';

/**
 * 商品グループの選択をクエリ文字列用の1つの値に直す。
 * `{タイプID}:{階層}` をカンマで連ねる（階層が最下層のときは省略）。空配列なら undefined。
 */
export function encodeProductGroupSelections(
  selections: ProductGroupSelection[],
): string | undefined {
  if (selections.length === 0) return undefined;
  return selections
    .map((s) => (s.depth === null ? s.type_id : `${s.type_id}:${s.depth}`))
    .join(',');
}

/**
 * 商品マスタ抽出プレビュー取得（先頭100件＋全件数）
 *
 * `selections` が空なら全タイプ×最下層（バックエンドの既定）。
 * `analysisTargetOnly` は母集団の絞り込み（既定は全SKU）。
 * `excludedColumnGroups` は出力から省く列のまとまり（既定は全列出力）。
 */
export async function getProductMasterPreview(
  accountCode: string,
  selections: ProductGroupSelection[] = [],
  authHeaders?: Record<string, string>,
  analysisTargetOnly = false,
  excludedColumnGroups: ProductMasterColumnGroup[] = [],
): Promise<ProductMasterPreview> {
  const encoded = encodeProductGroupSelections(selections);
  const params = new URLSearchParams();
  if (encoded) params.set('product_group_selections', encoded);
  // 全件CSVと母集団を揃える。ずれると「全◯件」の表示とCSVの行数が食い違う
  if (analysisTargetOnly) params.set('analysis_target_only', 'true');
  // 全件CSVと列構成を揃える。プレビューで見た列がそのままCSVの列になる契約を保つ
  if (excludedColumnGroups.length > 0) {
    params.set('excluded_column_groups', excludedColumnGroups.join(','));
  }
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiGet<ProductMasterPreview>(
    `/${accountCode}/product-master/preview${query}`,
    authHeaders,
  );
}
